"use client";

import React, { useState } from "react";
import { FiPhoneCall } from "react-icons/fi";
import { BsArrowUp } from "react-icons/bs";
import { TbBuilding } from "react-icons/tb";
import { HiOutlineMail } from "react-icons/hi";
import classNames from "classnames";
import Image from "next/image";
import { Collapse } from "antd";

// components
import Link from "@/components/Link";
import LandingSocials from "@/components/LandingSocials";
import LandingFooterSubscribeButton from "./components/LandingFooterSubscribeButton";
import SubscriptionModal from "@/components/Modals/SubscriptionModal";

//helpers
import { useDimension } from "@/hooks/useDimensions";
import useInternationalization from "@/hooks/useInternationalization";

// assets
import LoLiveLogoIcon from "@/components/icons/SupportUkraine/LoLiveLogoIcon";
import footerDotsGif from "@/assets/images/landing/footer-dots.gif";
import LoCationLogoIcon from "@/components/icons/SupportUkraine/LoCationLogoIcon";

import styles from "@/assets/styles/components/Footer/landing-footer.module.scss";

const LandingFooter = ({ className }: { className?: string }) => {
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);

  const [width] = useDimension();
  const dictionary = useInternationalization("footer");

  const footerClassNames = classNames(styles["locl-footer"], styles.Footer, className);
  const isTablet = width < 1200;
  const isMobile = width < 820;
  
  const headerColClassName = styles["locl-footer-heading__col"];
  const footerTextClassName = styles["locl-footer__text"];
  const footerTextSmallClassName = styles["locl-footer__text_small"];

  // methods
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // render
  const renderContactInfo = () => (
    <div
      className={classNames(
        headerColClassName,
        styles["locl-footer-contacts"]
      )}
    >
      <div className={styles.contact_info}>
        <HiOutlineMail />
        <a
          href={`mailto:${dictionary("contacts.email")}`}
          className={classNames(footerTextClassName, footerTextSmallClassName)}
        >
          {dictionary("contacts.email")}
        </a>
      </div>
      <div className={styles.contact_info}>
        <FiPhoneCall />
        <a
          href={`tel:${dictionary("contacts.phone.url")}`}
          className={classNames(footerTextClassName, footerTextSmallClassName)}
        >
          {dictionary("contacts.phone.label")}
        </a>
      </div>
      <div className={styles.contact_info}>
        <TbBuilding />
        <a
          href={`${dictionary("contacts.location.url")}`}
          className={classNames(
            footerTextClassName,
            footerTextSmallClassName,
            styles["locl-footer__text_no-underline"]
          )}
          target="_blank"
          rel="noreferrer"
        >
          {dictionary("contacts.location.label.firstLine")}
          <br />
          {dictionary("contacts.location.label.secondLine")}
        </a>
      </div>
    </div>
  )

  return (
    <div className={footerClassNames}>
      <div className={styles["locl-footer__animation-container"]}>
        <Image
          src={footerDotsGif}
          fill
          alt="Footer dots"
        />
      </div>
      <div className={styles["locl-footer-heading"]}>
        <div className={headerColClassName}>
          <div className={styles["locl-footer-heading__nav"]}>
            <div className={styles["locl-footer-heading__nav-list"]} data-testid="locl-footer-heading-nav-list">
              <Link href="/blog" className={classNames(footerTextClassName, styles["locl-footer-heading__nav-blog"])}>
                Blog
              </Link>
              <Link href="/collections" className={classNames(footerTextClassName, styles["locl-footer-heading__nav-collections"])}>
                Collections
              </Link>
              <Link href="/landlord" className={classNames(footerTextClassName, styles["locl-footer-heading__nav-landlord"])}>
                List your space
              </Link>
              <Link href="/blog/category/careers" className={classNames(footerTextClassName, styles["locl-footer-heading__nav-careers"])}>
                Careers
              </Link>
              <Link href="/support" className={classNames(footerTextClassName, styles["locl-footer-heading__nav-support"])}>
                Support
              </Link>
              {
                isMobile && (
                  <Collapse
                    rootClassName={styles["locl-footer-heading__nav-content-link"]}
                    expandIconPosition="right"
                    items={[{
                      key: "footer-content-link",
                      label: "Contact us",
                      children: (
                        <>
                          <LandingSocials />
                          {renderContactInfo()}
                        </>
                      ),
                    }]}
                  />
                )
              }
            </div>
            {isTablet && (
              <LandingFooterSubscribeButton className={styles["locl-footer-subscribe-btn"]} onButtonClick={() => setSubscribeModalOpen(true)} />
            )}
          </div>
          {
            !isMobile && (
              <div className={styles["locl-footer-heading__col-row"]}>
                <Link href="/" className="locl-footer-heading__logo-link" data-testid="locl-footer-heading-logo-lolive-group">
                  <LoCationLogoIcon className={styles.Footer__icon} textColor="#ffffff" />
                  <label className={styles["locl-footer-heading__logo-link-label"]}>Group</label>
                </Link>
                <Link href="/lolive" className="locl-footer-heading__logo-link" data-testid="locl-footer-heading-logo-lolive-digital">
                  <LoLiveLogoIcon className={styles.Footer__icon} textColor="#ffffff" />
                  <label className={styles["locl-footer-heading__logo-link-label"]}>
                    Digital platform
                  </label>
                </Link>
              </div>
            )
          }
        </div>
        {
          (!isTablet || !isMobile) && (
            <div
              className={classNames(
                headerColClassName,
                styles["locl-footer-heading__col_contacts"]
              )}
            >
              { !isMobile && renderContactInfo() }
              {!isTablet && (
                <LandingFooterSubscribeButton className={styles["locl-footer-subscribe-btn"]} onButtonClick={() => setSubscribeModalOpen(true)} />
              )}
            </div>
          )
        }
      </div>
      <div className={styles["locl-footer-bottom"]}>
        {!isMobile && <LandingSocials />}
        <div
          className={classNames(
            styles["locl-footer-bottom__row"],
            styles["locl-footer-bottom__row-nav"]
          )}
          data-testid="locl-footer-legal-links"
        >
          <Link
            href="/terms-of-use"
            target="_blank"
            className={footerTextClassName}
            rel="noreferrer"
          >
            Terms of Use
          </Link>
          <Link
            href="/bookingterms"
            target="_blank"
            className={footerTextClassName}
            rel="noreferrer"
          >
            Booking Terms
          </Link>
          <Link
            href="/privacy"
            target="_blank"
            className={footerTextClassName}
            rel="noreferrer"
          >
            Privacy Policy
          </Link>
          <Link
            href="/cookies"
            target="_blank"
            className={footerTextClassName}
            rel="noreferrer"
          >
            Cookie Notice
          </Link>
        </div>
        <div
          className={classNames(
            styles["locl-footer-bottom__row"],
            styles["locl-footer-bottom__row-copyright"]
          )}
        >
          <p className={classNames(footerTextClassName, footerTextSmallClassName)}>
            © {new Date().getFullYear()} Location Live
          </p>
          <span
            onClick={scrollToTop}
            className={classNames(
              styles["locl-footer__scroll-to-top"],
              footerTextClassName,
              footerTextSmallClassName
            )}
          >
            Back to the top
            <BsArrowUp />
          </span>
        </div>
      </div>
      <SubscriptionModal open={subscribeModalOpen} onCancel={() => setSubscribeModalOpen(false)} />
    </div>
  );
};

export default LandingFooter;
